import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5el9tallb01bjs85fs0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="mb-8">
              <span className="text-xs uppercase tracking-widest text-gray-500 font-medium">
                Contacto
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6 leading-tight">
              Hablemos sobre tu próxima <span className="text-[#e8a65b]">obra de arte</span>
            </h2>
            <p className="text-base text-gray-600 mb-12 leading-relaxed">
              ¿Interesado en adquirir una obra o realizar un encargo personalizado? Estoy aquí para ayudarte a encontrar la pieza perfecta para tu espacio.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFF8E7] rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-[#e8a65b] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">Email</h4>
                  <a href="mailto:info@davidnievesart.com" className="text-gray-600 hover:text-[#e8a65b] transition-colors text-sm cursor-pointer">
                    info@davidnievesart.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFF8E7] rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-[#e8a65b] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">Teléfono</h4>
                  <a href="tel:+17876043400" className="text-gray-600 hover:text-[#e8a65b] transition-colors text-sm cursor-pointer">
                    (787) 604-3400
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFF8E7] rounded-lg flex-shrink-0">
                  <i className="ri-instagram-line text-[#e8a65b] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">Instagram</h4>
                  <a 
                    href="https://www.instagram.com/davidnievesart/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-[#e8a65b] transition-colors text-sm cursor-pointer"
                  >
                    @davidnievesart
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FFF8E7] rounded-2xl p-8 lg:p-10">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#e8a65b] transition-colors"
                    placeholder="Tu nombre"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#e8a65b] transition-colors"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#e8a65b] transition-colors"
                    placeholder="(000) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-900 mb-2">
                    Asunto *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#e8a65b] transition-colors cursor-pointer"
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="compra">Compra de obra</option>
                    <option value="encargo">Encargo personalizado</option>
                    <option value="exposicion">Consulta sobre exposiciones</option>
                    <option value="colaboracion">Colaboración</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#e8a65b] transition-colors resize-none"
                    placeholder="Cuéntame sobre tu proyecto o consulta..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {formData.message.length}/500 caracteres
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#e8a65b] text-white rounded-lg hover:bg-[#d99549] transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-lg"></i>
                      ¡Mensaje enviado con éxito! Te responderé pronto.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 flex items-center gap-2">
                      <i className="ri-error-warning-line text-lg"></i>
                      Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}